/**
 * Lab-result code domain (verbatim port of the Portal lib): the single source of truth for the
 * FOBT/FIT result value (SNOMED CT, OBX-5) and status flag (HL7 v2, OBX-11) vocabularies.
 * Canonical reference: the Portal's _docs/RESULT-CODES-REFERENCE.md (William's attachment).
 *
 * The slicer axes key on these stable label sets (not on whatever subset a single data extract
 * happens to contain), so the option order is fixed and an axis only hides when a campaign has no
 * result data at all.
 */

export const POSITIVE_SNOMED = '10828004';

export interface SnomedResult {
  code: string;
  label: string;
  /** Screening-outcome bucket: a positive/negative completes the screen; invalid does not. */
  bucket: 'POSITIVE' | 'NEGATIVE' | 'INVALID';
}

/** SNOMED CT result values (OBX-5), in attachment order. */
export const SNOMED_RESULT: SnomedResult[] = [
  { code: '10828004', label: 'Positive', bucket: 'POSITIVE' },
  { code: '260385009', label: 'Negative', bucket: 'NEGATIVE' },
  { code: '82334004', label: 'Indeterminate', bucket: 'INVALID' },
  { code: '419984006', label: 'Inconclusive', bucket: 'INVALID' },
  { code: '385660001', label: 'Not done', bucket: 'INVALID' },
  { code: '125154007', label: 'Specimen unsatisfactory', bucket: 'INVALID' },
  { code: '281268007', label: 'Insufficient sample', bucket: 'INVALID' },
  { code: '451061000124104', label: 'Specimen leaked', bucket: 'INVALID' },
];

export interface Obx11Flag {
  flag: string;
  label: string;
}

/** HL7 v2 result status flags (OBX-11), in attachment order. */
export const OBX11_FLAG: Obx11Flag[] = [
  { flag: 'F', label: 'Final result' },
  { flag: 'P', label: 'Preliminary' },
  { flag: 'C', label: 'Corrected' },
  { flag: 'X', label: 'Canceled' },
  { flag: 'I', label: 'Pending / in process' },
  { flag: 'W', label: 'Wrong (post original as final)' },
];

const SNOMED_BY_CODE = new Map(SNOMED_RESULT.map((r) => [r.code, r]));
const FLAG_BY_CODE = new Map(OBX11_FLAG.map((f) => [f.flag, f]));

/** Fixed axis order for the "Result value" slicer (labels only). */
export const RESULT_VALUE_LABELS: string[] = SNOMED_RESULT.map((r) => r.label);

/** Fixed axis order for the "Result status flag" slicer (labels only). */
export const RESULT_STATUS_LABELS: string[] = OBX11_FLAG.map((f) => f.label);

/** SNOMED code -> human label. Unknown codes fall back to the raw code so an
 *  unexpected value still filters/displays rather than vanishing. Blank -> ''. */
export function snomedLabel(code: string | null | undefined): string {
  const c = (code ?? '').trim();
  if (!c) return '';
  return SNOMED_BY_CODE.get(c)?.label ?? c;
}

/** OBX-11 flag -> human label. Unknown flags fall back to the raw flag. */
export function flagLabel(flag: string | null | undefined): string {
  const f = (flag ?? '').trim();
  if (!f) return '';
  return FLAG_BY_CODE.get(f)?.label ?? f;
}

/** True for a positive FIT result (SNOMED 10828004). */
export function isPositiveSnomed(code: string | null | undefined): boolean {
  return (code ?? '').trim() === POSITIVE_SNOMED;
}
